/**
 * The confirm lane on the entity page. Each behaviour fills the page's own form through the same edits the entity chat
 * makes, then waits for the builder's click on the page's own button. Nothing here can save: the page reports what
 * its button did.
 *
 * ⚠ `import type` only for entity.model — see rules/entity-form-rules.ts.
 */
import type { Entity, TypeData } from '../../../model/entity.model';
import type { ToolAnswer } from '../mcp-types';
import type { EntityChatPage } from '../../../dashboard/entity/zs-entity/chat/entity-chat-session';
import type { EntityEdit } from '../../../dashboard/entity/zs-entity/zs-entity.types';
import type { ActionBehaviours } from '../tools/do-action.tool';
import { applyFieldEdit, linkTargetById } from '../../../dashboard/entity/zs-entity/apply/apply-edit';
import { canEditAllFieldSettings, canHaveSampleRows, isFinalized, isProtectedField, prepareNewEntityName } from '../../../dashboard/entity/zs-entity/rules/entity-form-rules';
import { entitySkeleton } from '../../../dashboard/entity/zs-entity/rules/field-builders';
import { ConfirmWaits, Prefilled, pressToCommit, PressToCommitDeps } from './confirm-lane';

/** The keys the entity page reports its commits under. */
export const ENTITY_COMMIT = 'entity';
export const ENTITY_PERMISSIONS_COMMIT = 'entity-permissions';
export const ENTITY_SYNC_COMMIT = 'entity-sync';
export const ENTITY_IMPORT_COMMIT = 'entity-import';
export const ENTITY_COMMITS = [ENTITY_COMMIT, ENTITY_PERMISSIONS_COMMIT, ENTITY_SYNC_COMMIT, ENTITY_IMPORT_COMMIT] as const;

export interface RolePermissionRow {
  roleId: string;
  create?: boolean;
  read?: boolean;
  update?: boolean;
  delete?: boolean;
}

/** What only the confirm lane asks of the page. Apart from EntityChatPage, which the chat's checks stand in for whole. */
export interface EntityConfirmPage {
  /** Opens Role Permissions with these rows ticked. The modal's own Save Permissions commits them. */
  prefillRolePermissions(rows: readonly RolePermissionRow[]): Promise<void>;
  /** Opens Predefined rows, so the builder sees the rows they are asked to approve. */
  showSampleRows(): void;
  /** Opens the import dialog for this item; false when the page doesn't offer it. */
  prefillImport(importId: string): boolean;
  syncRunning(): boolean;
}

export interface EntityConfirmDeps {
  bridge: { readonly page: EntityChatPage | null; readonly confirm: EntityConfirmPage | null };
  waits: Pick<ConfirmWaits, 'begin'>;
  highlight: PressToCommitDeps['highlight'];
}

/** What the wait hears from the page is the whole entity; an answer carries its shape, never its sample rows. */
export function savedEntitySummary(saved: unknown): unknown {
  const entity = saved as Entity;
  return { id: entity.id, name: entity.name, status: entity.status, fields: fieldsOf(entity.data) };
}

function fieldsOf(data: readonly TypeData[] | undefined): unknown[] {
  return (data ?? []).map((f) => (f.type === 'ARRAY_OF_OBJECT' ? { name: f.name, type: f.type, fields: fieldsOf(f.data) } : { name: f.name, type: f.type }));
}

/** The page's Update is the commit for a finalized entity, and Save for any other. */
export function saveButtonFor(entity: Pick<Entity, 'status'>): 'Save' | 'Update' {
  return isFinalized(entity.status) ? 'Update' : 'Save';
}

/** What the page has open that would sit over its buttons; null when nothing is in the way. */
export function entityPageBusy(page: EntityChatPage, then: string): string | null {
  const open = (what: string) => `${what} is open on the entity page. Nothing was changed. Ask the builder to finish or close it, then ${then}.`;
  if (page.fieldFormOpen()) return open('The field form');
  if (page.permissionsOpen()) return open('Role permissions');
  if (page.sampleRowsOpen()) return open('Predefined rows');
  return null;
}

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T;

function blankEdit(op: EntityEdit['op']): EntityEdit {
  return {
    op, entity: null, local: null, path: [], field: null, newName: null, type: null, reference: null,
    extendedAttributes: false, defaultValue: null, defaultMode: null, description: null, values: null, row: null, rowNumber: null,
  };
}

const CLOSED_PAGE = 'The entity page isn\'t on the builder\'s screen. Nothing was changed; call navigate to open it first.';

export function makeEntityConfirmBehaviours(deps: EntityConfirmDeps): ActionBehaviours {
  const press = (commit: string, prefill: PressToCommitDeps['prefill']) =>
    ({ confirm: pressToCommit({ waits: deps.waits, highlight: deps.highlight, commit, prefill }) });

  /** The entity open on the screen, with nothing over it; or why not. */
  const openEntity = (rowLabel: string): { page: EntityChatPage; open: Entity } | { refused: string } => {
    const page = deps.bridge.page;
    if (!page) return { refused: CLOSED_PAGE };
    const open = page.open();
    if (!open) return { refused: `No entity is open. Nothing was changed; call navigate to open it, then ${rowLabel}.` };
    const busy = entityPageBusy(page, `try ${rowLabel} again`);
    return busy ? { refused: busy } : { page, open };
  };

  /** Prefills through a draft of the open entity, which the page takes only when the whole edit was good. */
  const editDraft = (edit: (draft: Entity, page: EntityChatPage, values: Record<string, unknown>) => string | null, describe: (saved: unknown) => unknown = savedEntitySummary, after?: { note: string; shown: () => void }) =>
    press(ENTITY_COMMIT, async (values, _c, row): Promise<Prefilled> => {
      const here = openEntity(row.label);
      if ('refused' in here) return here;
      const draft = clone(here.open);
      const problem = edit(draft, here.page, values);
      if (problem) return { refused: `${problem} Nothing was changed.` };
      here.page.adopt(draft, 'open');
      after?.shown();
      return { button: saveButtonFor(draft), describe, ...(after ? { note: after.note } : {}) };
    });

  /** Commits what is already on the screen, optionally only in some statuses. */
  const asItIs = (problem: (open: Entity) => string | null, button?: string, verdict?: (saved: unknown, pressed: string) => ToolAnswer | null) =>
    press(ENTITY_COMMIT, async (_v, _c, row): Promise<Prefilled> => {
      const here = openEntity(row.label);
      if ('refused' in here) return here;
      const wrong = problem(here.open);
      if (wrong) return { refused: `${wrong} Nothing was changed.` };
      return { button: button ?? saveButtonFor(here.open), describe: savedEntitySummary, ...(verdict ? { verdict } : {}) };
    });

  const nameNewEntity = (values: Record<string, unknown>, draft: Entity, page: EntityChatPage): string | null => {
    const asked = values['entityName'];
    if (typeof asked !== 'string') return null;
    // The page offers its name input only until the first save, and every id and link is made from that name.
    if (draft.id) return `A saved entity's name can't be changed; ${draft.name} keeps its name.`;
    const prepared = prepareNewEntityName(asked, page.entities());
    if (!prepared.check.ok) return prepared.check.reason;
    draft.name = prepared.name;
    return null;
  };

  const namedDraft = () => press(ENTITY_COMMIT, async (values, _c, row): Promise<Prefilled> => {
    const here = openEntity(row.label);
    if ('refused' in here) return here;
    const draft = clone(here.open);
    const problem = nameNewEntity(values, draft, here.page);
    if (problem) return { refused: `${problem} Nothing was changed.` };
    if (typeof values['entityName'] === 'string') here.page.adopt(draft, 'open');
    return { button: saveButtonFor(draft), describe: savedEntitySummary };
  });

  return {
    'create-entity': press(ENTITY_COMMIT, async (values): Promise<Prefilled> => {
      const page = deps.bridge.page;
      if (!page) return { refused: CLOSED_PAGE };
      if (page.open()) return { refused: 'An entity is already open. Nothing was changed; ask the builder to finish it first.' };
      const kind = values['entityKind'] ?? 'database';
      if (kind !== 'database' && kind !== 'local') return { refused: 'entityKind is database or local. Nothing was changed.' };
      const prepared = prepareNewEntityName(String(values['entityName'] ?? ''), page.entities());
      if (!prepared.check.ok) return { refused: `${prepared.check.reason} Nothing was changed.` };
      const draft = entitySkeleton(kind);
      draft.name = prepared.name;
      page.adopt(draft, 'new');
      return { button: 'Save', describe: savedEntitySummary };
    }),
    'rename-entity': namedDraft(),
    'save-entity': namedDraft(),
    'update-entity': asItIs((open) => (isFinalized(open.status) ? null : `${open.name} isn't finalized, so it has Save rather than Update.`), 'Update', notUpdated),
    'finalize-entity': asItIs((open) => (open.status === 'NEW' ? null : `${open.name} can't be finalized from ${open.status}.`), 'Finalize', notFinalized),

    'edit-entity-field': editDraft((draft, page, values) => {
      const out = applyFieldEdit(draft, {
        ...blankEdit('edit_field'),
        field: asText(values['fieldName']),
        newName: asText(values['newName']),
        type: asText(values['fieldType']),
        description: asText(values['description']),
      }, { entities: page.entities(), mint: () => page.mint(), nowIso: page.now().toISOString(), linkTarget: linkTargetById });
      return out.changes.length ? null : out.declined.join(' ') || `Couldn't change ${String(values['fieldName'])}.`;
    }),
    'delete-entity-field': editDraft((draft, _page, values) => {
      const name = asText(values['fieldName']) ?? '';
      const index = draft.data.findIndex((f) => f.name.toLowerCase() === name.toLowerCase());
      if (index < 0) return `${draft.name} has no field '${name}'.`;
      // The page draws its delete only while every setting is editable, which ends at Finalize.
      if (!canEditAllFieldSettings(draft)) return `${draft.name} is finalized, so its fields can't be deleted.`;
      if (isProtectedField(draft.data[index].name)) return `${draft.data[index].name} is a system field and can't be deleted.`;
      draft.data.splice(index, 1);
      return null;
    }),
    // The commit button sits under the dialog, so the note says to close it first; a mark's ring draws above the dialog.
    'set-predefined-rows': editDraft((draft, page, values) => {
      if (!canHaveSampleRows(draft)) return `${draft.name} can't hold predefined rows: only a local entity with at least one plain field can.`;
      draft.predefinedData = [];
      const rows = values['rows'] as Record<string, string | number | boolean | null>[] | undefined;
      for (const values of rows ?? []) {
        const out = applyFieldEdit(draft, { ...blankEdit('add_row'), values }, { entities: page.entities(), mint: () => page.mint(), nowIso: page.now().toISOString(), linkTarget: linkTargetById });
        if (out.declined.length) return out.declined.join(' ');
      }
      return null;
    }, (saved) => ({ ...(savedEntitySummary(saved) as object), rows: (saved as Entity).predefinedData ?? [] }), {
      note: 'Check these rows, close this window, then press this. Claude can\'t press it for you.',
      shown: () => deps.bridge.confirm?.showSampleRows(),
    }),

    'set-role-permissions': press(ENTITY_PERMISSIONS_COMMIT, async (values, _c, row): Promise<Prefilled> => {
      const here = openEntity(row.label);
      if ('refused' in here) return here;
      if (!here.open.id) return { refused: `${here.open.name || 'The new entity'} has to be saved before it can have role permissions. Nothing was changed.` };
      const rows = (values['rolePermissions'] ?? []) as RolePermissionRow[];
      const known = new Set(here.page.roles().map((r) => r.id));
      const stranger = rows.find((r) => !known.has(r.roleId));
      if (stranger) return { refused: `There's no role '${stranger.roleId}'. Nothing was changed.` };
      if (!deps.bridge.confirm) return { refused: CLOSED_PAGE };
      await deps.bridge.confirm.prefillRolePermissions(rows);
      return {};
    }),
    'sync-entities': press(ENTITY_SYNC_COMMIT, async (): Promise<Prefilled> => {
      const page = deps.bridge.page;
      if (!page || !deps.bridge.confirm) return { refused: CLOSED_PAGE };
      if (page.open()) return { refused: 'Sync is on the entity list, and an entity is open. Nothing was changed; ask the builder to close it first.' };
      if (deps.bridge.confirm.syncRunning()) return { refused: 'Sync is already running. Nothing was changed.' };
      return {};
    }),
    'import-entities': press(ENTITY_IMPORT_COMMIT, async (values): Promise<Prefilled> => {
      const page = deps.bridge.page;
      if (!page || !deps.bridge.confirm) return { refused: CLOSED_PAGE };
      if (page.open()) return { refused: 'Import is on the entity list, and an entity is open. Nothing was changed; ask the builder to close it first.' };
      const id = String(values['importId'] ?? '');
      if (!deps.bridge.confirm.prefillImport(id)) return { refused: `'${id}' isn't something to import here, or it's already imported. Nothing was changed.` };
      return {};
    }),
  };
}

// Save, Update and Finalize report under one commit, so a wait for one can end on another. Saved is true, and the row's word isn't.
function notFinalized(saved: unknown, pressed: string): ToolAnswer | null {
  const entity = saved as Entity;
  if (isFinalized(entity.status)) return null;
  return {
    outcome: 'declined',
    by: 'builder',
    result: {
      outcome: 'saved-not-finalized',
      pressed,
      saved: savedEntitySummary(saved),
      message: `The builder pressed ${pressed}, not Finalize. ${entity.name} saved, and its status is ${entity.status}, so it is not finalized. Read the screen before saying more.`,
    },
  };
}

function notUpdated(saved: unknown, pressed: string): ToolAnswer | null {
  if (pressed === 'Update') return null;
  const entity = saved as Entity;
  return {
    outcome: 'declined',
    by: 'builder',
    result: {
      outcome: 'saved-not-updated',
      pressed,
      saved: savedEntitySummary(saved),
      message: `The builder pressed ${pressed}, not Update. ${entity.name} saved, and its status is ${entity.status}, but Update itself didn't run. Read the screen before saying more.`,
    },
  };
}

const asText = (v: unknown): string | null => (typeof v === 'string' ? v : null);
